/**
 * CommissionSign — Public rep sign-off page at /commissions/sign/:token.
 *
 * No auth; the emailed magic token gates access. Shows the statement summary
 * + accrual detail, then POSTs a typed signature to
 * /api/public/commission-statements/{token}/sign.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, FileText, ShieldCheck, AlertCircle, Loader2 } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const money = (v) => Number(v || 0).toLocaleString(undefined, { style: "currency", currency: "USD" });

export default function CommissionSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [signerName, setSignerName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    if (document.getElementById("gf-signature-fonts")) return;
    const link = document.createElement("link");
    link.id = "gf-signature-fonts";
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Caveat&display=swap";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    axios.get(`${API_BASE}/api/public/commission-statements/${token}`)
      .then((r) => {
        setData(r.data);
        setSignerName(r.data?.rep_name || "");
        if (r.data?.already_signed) setSigned(true);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Statement not found or link expired"));
  }, [token]);

  const sign = async () => {
    if (!accepted || !signerName.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/api/public/commission-statements/${token}/sign`, {
        signer_name: signerName.trim(),
        signature_text: signerName.trim(),
        signature_font: "Caveat",
        accepted: true,
      });
      setSigned(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Signing failed — please try again");
    } finally {
      setBusy(false);
    }
  };

  if (error && !data) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
        <div className="bg-white border border-red-200 rounded-sm p-8 max-w-md text-center" data-testid="commission-sign-error">
          <AlertCircle className="w-10 h-10 text-red-600 mx-auto mb-3" />
          <div className="font-bold text-zinc-950">{error}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center gap-2 text-zinc-500">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading statement…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100 py-8 px-4" data-testid="commission-sign-page">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="bg-zinc-950 text-white rounded-sm px-6 py-5">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-400 mb-1">SealTech Building Solutions</div>
          <h1 className="font-heading text-2xl font-black tracking-tight">Commission Statement</h1>
          <div className="text-sm text-zinc-300 mt-1">
            {data.rep_name} · {data.period_start} → {data.period_end}
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Total Earned This Period</div>
              <div className="font-heading text-4xl font-black text-emerald-700 tabular-nums" data-testid="commission-sign-total">{money(data.total)}</div>
            </div>
            <a
              href={`${API_BASE}/api/public/commission-statements/${token}/pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="commission-sign-pdf-link"
              className="inline-flex items-center gap-2 h-10 px-4 text-xs font-bold uppercase tracking-wider border border-zinc-300 text-zinc-800 hover:bg-zinc-100 rounded-sm"
            >
              <FileText className="w-4 h-4" /> View PDF
            </a>
          </div>

          <table className="w-full text-sm border-t border-zinc-200">
            <thead className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-2 py-2">Date</th>
                <th className="text-left px-2 py-2">Project</th>
                <th className="text-left px-2 py-2">Type</th>
                <th className="text-right px-2 py-2">Collected</th>
                <th className="text-right px-2 py-2">Rate</th>
                <th className="text-right px-2 py-2">Earned</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {(data.accruals || []).map((a, i) => (
                <tr key={i}>
                  <td className="px-2 py-2 whitespace-nowrap text-zinc-600">{(a.accrued_at || "").split("T")[0]}</td>
                  <td className="px-2 py-2 font-medium">{a.deal_title}</td>
                  <td className="px-2 py-2 text-xs text-zinc-500 uppercase">{a.kind}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums">{money(a.payment_amount_collected)}</td>
                  <td className="px-2 py-2 text-right text-zinc-600">{Number(a.rate_pct_at_time || 0).toFixed(2)}%</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums font-bold text-emerald-700">{money(a.commission_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {signed ? (
          <div className="bg-emerald-50 border border-emerald-300 rounded-sm p-6 text-center" data-testid="commission-sign-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-700 mx-auto mb-2" />
            <div className="font-bold text-emerald-900 text-lg">Statement signed</div>
            <div className="text-sm text-emerald-800 mt-1">
              {data.signed_by_name || signerName} — your signature is on file. Payment will be processed by the office.
            </div>
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-700" />
              <h2 className="font-heading text-lg font-bold tracking-tight">Review &amp; Sign</h2>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Your Full Name</label>
              <input
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                data-testid="commission-sign-name"
                className="w-full h-11 px-3 border border-zinc-300 rounded-sm text-sm"
              />
              {signerName.trim() && (
                <div className="mt-2 border border-dashed border-zinc-300 rounded-sm px-4 py-2 text-3xl" style={{ fontFamily: "'Caveat', cursive" }}>
                  {signerName}
                </div>
              )}
            </div>
            <label className="flex items-start gap-2 text-sm text-zinc-700 cursor-pointer">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                data-testid="commission-sign-accept"
                className="mt-0.5"
              />
              <span>
                I have reviewed this commission statement for {data.period_start} through {data.period_end} and agree
                the total of <b>{money(data.total)}</b> is accurate.
              </span>
            </label>
            {error && <div className="text-sm text-red-700">{error}</div>}
            <button
              onClick={sign}
              disabled={busy || !accepted || !signerName.trim()}
              data-testid="commission-sign-submit"
              className="w-full h-12 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-bold uppercase tracking-wider rounded-sm disabled:opacity-40 inline-flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Sign Statement
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
